import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { CancelButton, ModalComponent, SaveButton, trans } from "@comps/components";
import { makeRequest, makeRequestStateless } from "@utils/helpers";
import { createModuleReq, getModulesReq } from "../requests";
import { Commons } from "../../../constants";
import { makeFieldLabel } from "@utils/fieldTooltips";
import { masterTooltips } from "../../../../common/masterTooltips";

const formName = "createModule";

const fieldLabel = makeFieldLabel(masterTooltips.role, { namespace: "role" });

const ManageModules = (props) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [modules, setModules] = useState([]);
  const [actionTemplates, setActionTemplates] = useState([]);
  const [actions, setActions] = useState(getDefaultActions());

  useEffect(() => {
    makeRequestStateless(getModulesReq, null, onFetchSuccess, onFetchError);
  }, []);

  const onFetchSuccess = (response) => {
    setModules(response.modules || []);
    setActionTemplates(response.actions || []);
  };

  const onFetchError = (err, res) => {
    message.error(res?.response?.data?.message || "Unable to load modules");
  };

  const parentOptions = useMemo(
    () => (modules || []).map((module) => ({
      label: prettifyText(module.description || module.name),
      value: module.id,
    })),
    [modules]
  );

  const onAddAction = (action = null) => {
    setActions((prev) => ([
      ...prev,
      {
        id: action?.id || 0,
        key: `${Date.now()}-${Math.random()}`,
        name: action?.name || "",
        description: action?.description || prettifyText(action?.name || ""),
        new: true,
      },
    ]));
  };

  const onRemoveAction = (key) => {
    setActions((prev) => prev.filter((action) => action.key !== key));
  };

  const onActionChange = (key, field, value) => {
    setActions((prev) => prev.map((action) => (
      action.key === key ? { ...action, [field]: value } : action
    )));
  };

  const onAddTemplateActions = () => {
    const existingNames = new Set(actions.map((action) => slugifyActionName(action.name)));
    const nextActions = [...actions];

    actionTemplates.forEach((template) => {
      const slugName = slugifyActionName(template.name);
      if (!existingNames.has(slugName)) {
        existingNames.add(slugName);
        nextActions.push({
          id: template.id,
          key: `${Date.now()}-${template.id}-${Math.random()}`,
          name: template.name,
          description: template.description || prettifyText(template.name),
          new: true,
        });
      }
    });

    setActions(nextActions);
  };

  const onSubmit = (values) => {
    const cleanedActions = actions
      .map((action) => ({
        id: action.id || 0,
        name: slugifyActionName(action.name),
        description: (action.description || action.name || "").trim(),
        new: true,
        selected: 0,
      }))
      .filter((action) => action.name);

    const payload = {
      name: slugifyModuleName(values.name),
      description: (values.description || values.name || "").trim(),
      ui_sref: (values.ui_sref || "").trim(),
      parent_id: values.parent_id || null,
      is_active: values.is_active !== false,
      is_mobile: false,
      temp_user: false,
      actions: cleanedActions,
    };

    setSaving(true);
    makeRequest(setSaving, createModuleReq, payload, onCreateSuccess, onCreateError);
  };

  const onCreateSuccess = (response) => {
    setSaving(false);
    message.success("Module created successfully");
    props.onCompleted(response);
  };

  const onCreateError = (err, res) => {
    setSaving(false);
    message.error(res?.response?.data?.message || "Unable to create module");
  };

  const footer = [
    <SaveButton loading={ saving } form={ formName } key="create_button" htmlType="submit" />,
    <CancelButton key="close_button" onClick={ props.onCancel } />
  ];

  return (
    <ModalComponent
      top={ 20 }
      width={ 900 }
      maskClosable={ false }
      mainTitle={ trans(Commons.create) }
      subTitle="Module and Actions"
      open={ true }
      footer={ footer }
      onCancel={ props.onCancel }
    >
      <Form
        form={ form }
        name={ formName }
        layout="vertical"
        onFinish={ onSubmit }
      >
        <div className="module-manager-grid">
          <Form.Item
            name="name"
            label={ fieldLabel("name", "Module key") }
            rules={ [{ required: true, message: "Module key is required" }] }
          >
            <Input placeholder="example: company-roster" />
          </Form.Item>

          <Form.Item
            name="description"
            label={ fieldLabel("description", trans(Commons.description)) }
            rules={ [{ required: true, message: "Description is required" }] }
          >
            <Input placeholder="Example: Company Roster" />
          </Form.Item>

          <Form.Item
            name="ui_sref"
            label={ fieldLabel("ui_sref", "UI Sref") }
          >
            <Input placeholder="Example: app.companyRoster" />
          </Form.Item>

          <Form.Item name="parent_id" label={ fieldLabel("parent_id", "Parent module") }>
            <Select
              allowClear
              showSearch
              placeholder="Select parent module"
              options={ parentOptions }
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label={ fieldLabel("is_active", "Status") }
            valuePropName="checked"
            initialValue={ true }
          >
            <Checkbox>Active module</Checkbox>
          </Form.Item>
        </div>

        <Divider orientation="left">Actions</Divider>

        <div className="module-manager-actions-header">
          <div>
            <Typography.Text strong>Attach the actions you want to control for this module.</Typography.Text>
            <div className="module-manager-hints">
              Common examples: <Tag>index</Tag><Tag>create</Tag><Tag>update</Tag><Tag>view</Tag><Tag>delete</Tag>
            </div>
          </div>
          <Space wrap>
            <Button onClick={ onAddTemplateActions }>Use common actions</Button>
            <Button type="primary" ghost icon={ <PlusOutlined /> } onClick={ () => onAddAction() }>
              Add action
            </Button>
          </Space>
        </div>

        <div className="module-manager-actions-list">
          { actions.map((action) => (
            <div key={ action.key } className="module-manager-action-row">
              <Input
                value={ action.name }
                onChange={ (event) => onActionChange(action.key, "name", event.target.value) }
                placeholder="Action key"
              />
              <Input
                value={ action.description }
                onChange={ (event) => onActionChange(action.key, "description", event.target.value) }
                placeholder="Action label"
              />
              <Button
                danger
                type="text"
                icon={ <DeleteOutlined /> }
                onClick={ () => onRemoveAction(action.key) }
              />
            </div>
          )) }
        </div>
      </Form>
    </ModalComponent>
  );
};

export default ManageModules;

const getDefaultActions = () => ([
  { id: 0, key: "action-index", name: "index", description: "Index", new: true },
  { id: 0, key: "action-create", name: "create", description: "Create", new: true },
  { id: 0, key: "action-update", name: "update", description: "Update", new: true },
  { id: 0, key: "action-view", name: "view", description: "View", new: true },
  { id: 0, key: "action-delete", name: "delete", description: "Delete", new: true },
]);

const slugifyModuleName = (value = "") => {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/_+/g, "-");
};

const slugifyActionName = (value = "") => {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_");
};

const prettifyText = (value = "") => {
  return String(value)
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};
